import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { approvalsApi } from '@/services/approvalsApi';
import { timesheetApi } from '@/services/timesheetApi';
import { getDelegationService } from '@/features/delegation/services/delegationService';
import type { ApprovalDelegation } from '@/features/delegation/types/delegation.types';
import type { Employee, Project, Timesheet } from '@/types/timesheet';
import type { ApprovingForApprover } from '@/components/ApprovalDashboard/ApprovingForSwitcher';
import { SELECTABLE_PROJECT_STATUSES } from '@/utils/ApprovalDashboard/constants';

interface UseApprovalQueueDataParams {
  screenMode: 'demo' | 'live';
  timesheets: Timesheet[];
  projects: Project[];
  currentEmployee: Employee;
}

export interface UseApprovalQueueDataReturn {
  activeDelegations: ApprovalDelegation[];
  isLoadingPending: boolean;
  pendingTimesheets: Timesheet[];
  /** Live mode: approvers whose queues the user may approve from (Approve on Behalf of Others). */
  approvableFor: ApprovingForApprover[];
  effectiveProjects: Project[];
  selectableProjects: Project[];
  /** Bumped whenever the project/cost-centre data is refetched. */
  projectDataEpoch: number;
  refreshPendingApprovals: () => Promise<void>;
}

/**
 * Owns the approval queue's remote data: delegations, server-side pending
 * approvals and the full project/cost-centre list used by the review dialog.
 */
export function useApprovalQueueData({
  screenMode,
  timesheets,
  projects,
  currentEmployee,
}: UseApprovalQueueDataParams): UseApprovalQueueDataReturn {
  // Server-side pending approvals state (for live mode)
  const [serverPendingTimesheets, setServerPendingTimesheets] = useState<Timesheet[]>([]);
  const [isLoadingPending, setIsLoadingPending] = useState(false);

  const [approvableFor, setApprovableFor] = useState<ApprovingForApprover[]>([]);

  // Active delegations — used to tag rows with "Delegated From" manager name
  const [activeDelegations, setActiveDelegations] = useState<ApprovalDelegation[]>([]);

  // All projects and cost centres fetched specifically for the approval queue.
  // The context `projects` may be paginated or scoped to the approver, so we
  // fetch the full set here to ensure costing details display correctly and
  // the find-screen selectors return results when editing.
  const [approvalProjects, setApprovalProjects] = useState<Project[]>([]);
  const [projectDataEpoch, setProjectDataEpoch] = useState(0);

  // Fetch active delegations (delegated-to-me) for queue segmentation (FE-14)
  // In live mode, also fetch pending approvals (including delegated timesheets)
  // in the same flow so the API receives the delegation context.
  useEffect(() => {
    const delegationSvc = getDelegationService(screenMode === 'live' ? 'live' : 'demo');

    if (screenMode === 'live') {
      setIsLoadingPending(true);

      // Fetch delegations first so we can pass manager IDs to the approvals API
      (async () => {
        try {
          const delegations = await delegationSvc.getDelegatedToMe();
          const active = delegations.filter((d) => d.status === 'active');
          setActiveDelegations(active);
          const managerIds = active.map((d) => String(d.managerId));

          const [pendingResult, allProjects, allCostCentres, approvers] = await Promise.all([
            approvalsApi.getPendingApprovals(managerIds),
            timesheetApi.getAllProjects(),
            timesheetApi.getAllCostCentres(),
            approvalsApi.getApprovableFor().catch((error) => {
              console.error('Failed to fetch approvers you can approve for:', error);
              return [] as ApprovingForApprover[];
            }),
          ]);
          setServerPendingTimesheets(pendingResult);
          setApprovableFor(approvers);
          setApprovalProjects(allProjects.map(p => ({ ...p, costCentres: allCostCentres })));
        } catch (error) {
          console.error('Failed to fetch pending approvals:', error);
          toast.error('Failed to load pending approvals');
          setActiveDelegations([]);
        } finally {
          setIsLoadingPending(false);
        }
      })();
    } else {
      // Demo mode — just fetch delegations
      delegationSvc.getDelegatedToMe()
        .then((delegations) => {
          setActiveDelegations(delegations.filter((d) => d.status === 'active'));
        })
        .catch(() => {
          setActiveDelegations([]);
        });
    }
  }, [screenMode]);

  // Listen for company-changed event to refetch data when switching companies.
  // Debounced to coalesce rapid switches and avoid stale-context 403s.
  useEffect(() => {
    let debounceTimer: ReturnType<typeof setTimeout>;
    const delegationSvc = getDelegationService(screenMode === 'live' ? 'live' : 'demo');

    const handleCompanyChange = (event: Event) => {
      clearTimeout(debounceTimer);
      const signal = (event as CustomEvent).detail?.signal as AbortSignal | undefined;

      debounceTimer = setTimeout(async () => {
        if (signal?.aborted) return;
        if (screenMode === 'live') {
          setIsLoadingPending(true);
          try {
            // Delegations are company scoped, so refetch them before deriving
            // the manager IDs the approvals API is queried with.
            const delegations = await delegationSvc.getDelegatedToMe();
            if (signal?.aborted) return;
            const active = delegations.filter((d) => d.status === 'active');
            setActiveDelegations(active);
            const managerIds = active.map((d) => String(d.managerId));

            const [pendingResult, allProjects, allCostCentres] = await Promise.all([
              approvalsApi.getPendingApprovals(managerIds),
              timesheetApi.getAllProjects(),
              timesheetApi.getAllCostCentres(),
            ]);
            if (signal?.aborted) return;
            setServerPendingTimesheets(pendingResult);
            setApprovalProjects(allProjects.map(p => ({ ...p, costCentres: allCostCentres })));
            setProjectDataEpoch(epoch => epoch + 1);
          } catch (error) {
            if (signal?.aborted) return;
            console.error('Failed to fetch pending approvals after company change:', error);
            toast.error('Failed to load pending approvals');
          } finally {
            setIsLoadingPending(false);
          }
        }
      }, 150);
    };

    window.addEventListener('company-changed', handleCompanyChange);
    return () => {
      clearTimeout(debounceTimer);
      window.removeEventListener('company-changed', handleCompanyChange);
    };
  }, [screenMode]);

  // In live mode prefer the full project list fetched specifically for the
  // approval queue; fall back to context projects for demo mode.
  const effectiveProjects = useMemo(
    () => (screenMode === 'live' && approvalProjects.length > 0 ? approvalProjects : projects),
    [screenMode, approvalProjects, projects]
  );

  // Projects offered by the edit-mode selector. Live-mode projects carry a
  // `status` string and only populate `active` when the source system reports a
  // status the backend recognises, so prefer the status (matching
  // TimesheetEntryStatusValidator) and fall back to `active` when absent.
  const selectableProjects = useMemo(
    () => effectiveProjects.filter(p => {
      const status = p.status?.trim().toLowerCase();
      return status ? SELECTABLE_PROJECT_STATUSES.includes(status) : p.active !== false;
    }),
    [effectiveProjects]
  );

  // Helper function to refresh pending approvals in live mode
  const refreshPendingApprovals = useCallback(async () => {
    if (screenMode === 'live') {
      try {
        const managerIds = activeDelegations.map((d) => String(d.managerId));
        const approvals = await approvalsApi.getPendingApprovals(managerIds);
        setServerPendingTimesheets(approvals);
      } catch (error) {
        console.error('Failed to refresh pending approvals:', error);
      }
    }
  }, [screenMode, activeDelegations]);

  // Get timesheets assigned to current user as approver - workflow-aware filtering
  // In live mode, use server-side filtered data; in demo mode, use client-side filtering
  const pendingTimesheets = useMemo(() => {
    if (screenMode === 'live') {
      // Use server-side filtered data in live mode
      return serverPendingTimesheets;
    }
    // Client-side filtering for demo mode.
    // Include:
    //  (a) timesheets directly assigned to the current user as approver
    //  (b) timesheets assigned to any manager who has an active delegation to the
    //      current user — these will be tagged via delegationManagerId on approvalRows
    //      and routed to a segmented section by SegmentedApprovalsQueue.
    const delegatedManagerIds = new Set(
      activeDelegations
        .filter((d) => d.status === 'active')
        .map((d) => String(d.managerId))
    );

    return timesheets.filter(ts => {
      // Only show submitted (pending) timesheets
      if (ts.status !== 'submitted') return false;

      // (a) Own approvals
      if (ts.approverId === currentEmployee.id) return true;

      // (b) Delegated approvals — ts.approverId belongs to a delegating manager
      const tsApproverId = ts.delegatedApproverId ?? ts.approverId;
      if (tsApproverId !== undefined && delegatedManagerIds.has(String(tsApproverId))) {
        return true;
      }

      return false;
    });
  }, [screenMode, serverPendingTimesheets, timesheets, currentEmployee, activeDelegations]);

  return {
    activeDelegations,
    isLoadingPending,
    pendingTimesheets,
    approvableFor,
    effectiveProjects,
    selectableProjects,
    projectDataEpoch,
    refreshPendingApprovals,
  };
}
